import React, { useMemo } from "react";
import { TickerChart } from "../../core/TickerChart";
import styles from "./styles.module.css";
import { useRealtimeMarketData, useTickerChartData } from "~hooks/ticker-chart";
import type {
  ChartAxisFormatters,
  ChartType,
  DataInterval,
  HoverEvent,
  TickerSeries,
  TickerChartDateRange,
} from "~types/ticker-chart";
import { isTickerActive } from "~utils/tickerStatus";

const PRIMARY_TICKER_COLOR = "#000000";

type ModeAwareTickerChartProps = {
  ticker: string;
  dateRange: TickerChartDateRange;
  chartType: ChartType;
  interval?: DataInterval;
  height?: number;
  width?: number;
  enableRealTime: boolean;
  enableVolume: boolean;
  enableZoomPan: boolean;
  comparisonTickers?: string[];
  showBaseline: boolean;
  showMultiColorLine: boolean;
  onHover?: (event: HoverEvent) => void;
  formatters?: ChartAxisFormatters;
};

function ChartSkeleton({ height, width }: { height: number; width: number }) {
  return (
    <div
      className={styles.skeleton}
      style={{ height: `${height}px`, width: `${width}px` }}
      role="status"
      aria-label="Loading chart data"
    >
      <div className={styles.skeletonHeader}>
        <div className={styles.skeletonTitle} />
        <div className={styles.skeletonSubtitle} />
      </div>
      <div className={styles.skeletonChart} />
      <div className={styles.skeletonAxis} />
    </div>
  );
}

function ChartError({ message }: { message: string }) {
  return (
    <div className={styles.error} role="alert">
      <span className={styles.errorIcon}>!</span>
      <p className={styles.errorMessage}>{message}</p>
    </div>
  );
}

function ChartWarnings({ warnings }: { warnings: string[] }) {
  if (warnings.length === 0) return null;

  return (
    <div className={styles.warnings} role="status">
      {warnings.map((warning, index) => (
        <p key={index} className={styles.warningMessage}>
          {warning}
        </p>
      ))}
    </div>
  );
}

export default function ModeAwareTickerChart(props: ModeAwareTickerChartProps) {
  const {
    ticker,
    dateRange,
    chartType,
    height = 405,
    width = 618,
    comparisonTickers = [],
    enableRealTime,
    enableVolume,
    enableZoomPan,
    showBaseline,
    showMultiColorLine,
    onHover,
    formatters,
  } = props;

  const isRealtimeTab = dateRange === "1H";

  const crosshairConfig = useMemo(
    () => ({ showVertical: true, showHorizontal: false }),
    []
  );

  const interactionConfig = useMemo(
    () => ({
      zoomEnabled: enableZoomPan,
      panEnabled: enableZoomPan,
      hoverEnabled: true,
    }),
    [enableZoomPan]
  );

  const realtimeConfig = useMemo(
    () =>
      isRealtimeTab
        ? {
            autoScroll: true,
            autoScrollBehavior: "scroll" as const,
            showRealtimeDot: true,
          }
        : undefined,
    [isRealtimeTab]
  );

  const {
    data: upstreamData,
    dataInterval,
    status,
    error,
  } = useTickerChartData(
    [ticker, ...comparisonTickers],
    dateRange,
    enableVolume,
    chartType === "candlestick"
  );

  const dataConfig = useMemo(
    () => ({ fillGaps: true, interval: dataInterval }),
    [dataInterval]
  );

  const { onReady: handleChartReady } = useRealtimeMarketData({
    ticker,
    chartType,
    enableRealTime,
  });

  // Process data and generate warnings for comparison tickers
  const { validSeries, warnings, mainTickerError } = useMemo(() => {
    const result: {
      validSeries: TickerSeries[];
      warnings: string[];
      mainTickerError: string | null;
    } = {
      validSeries: [],
      warnings: [],
      mainTickerError: null,
    };

    if (!upstreamData || upstreamData.length === 0) {
      return result;
    }

    // Process each ticker
    upstreamData.forEach((series, index) => {
      const isMainTicker = index === 0;
      const tickerId = series.id ?? series.name;

      // Check market status
      if (!isTickerActive(series.marketStatus ?? "")) {
        if (isMainTicker) {
          result.mainTickerError = `${tickerId} is currently inactive (status: ${series.marketStatus})`;
        } else {
          result.warnings.push(
            `Comparison ticker ${tickerId} is inactive (status: ${series.marketStatus})`
          );
        }

        return;
      }

      // Check for price data
      if (!series.priceMovements.length) {
        if (isMainTicker) {
          result.mainTickerError = `No price data available for ticker: ${tickerId}`;
        } else {
          result.warnings.push(
            `No price data for comparison ticker: ${tickerId}`
          );
        }

        return;
      }

      if (dateRange === "1H") {
        // only retain the last 1-hour series data
        // eslint-disable-next-line no-param-reassign
        series.priceMovements = series.priceMovements.slice(-60);
      }

      result.validSeries.push(series);
    });

    return result;
  }, [dateRange, upstreamData]);

  // Render loading state
  if (status === "pending") {
    return (
      <div className={styles.fadeIn}>
        <ChartSkeleton height={height} width={width} />
      </div>
    );
  }

  // Render error state from fetch
  if (status === "error") {
    return <ChartError message={error.message} />;
  }

  // Render empty state when no data returned
  if (!upstreamData || upstreamData.length === 0) {
    return <ChartError message={`No data available for ticker: ${ticker}`} />;
  }

  // Render main ticker error (inactive or no price data)
  if (mainTickerError) {
    return <ChartError message={mainTickerError} />;
  }

  // Render empty state when main ticker was filtered out
  if (validSeries.length === 0) {
    return <ChartError message={`No data available for ticker: ${ticker}`} />;
  }

  const intraday = dateRange === "1D";

  // Determine chart mode based on valid series count
  const isComparisonMode = validSeries.length > 1;

  // In comparison mode, override primary ticker color to black
  const displaySeries = isComparisonMode
    ? validSeries.map((s, i) =>
        i === 0 ? { ...s, color: PRIMARY_TICKER_COLOR } : s
      )
    : validSeries;

  // Build chart props based on mode
  const chartProps = isComparisonMode
    ? {
        mode: "comparison" as const,
        series: displaySeries as [
          TickerSeries,
          TickerSeries,
          ...TickerSeries[]
        ],
        comparison: { valueMode: "percentage" as const },
      }
    : {
        mode: "single" as const,
        series: validSeries[0],
        chartType,
        showGradient: true,
        showBaseline,
        showMultiColorLine,
        showLastVisiblePrice: true,
        usePreviousCloseAsBaseline: intraday,
        volume: {
          enabled: enableVolume,
        },
      };

  return (
    <div className={styles.container}>
      <ChartWarnings warnings={warnings} />
      <TickerChart
        {...chartProps}
        height={height}
        width={width}
        viewportEndBuffer={0.05}
        xAxisTickSize={6}
        yAxisTickSize={5}
        intraday={intraday}
        animate={false}
        dataConfig={dataConfig}
        crosshair={crosshairConfig}
        interactions={interactionConfig}
        realtime={realtimeConfig}
        formatters={formatters}
        onHover={onHover}
        onReady={handleChartReady}
      />
    </div>
  );
}
